import "@fontsource-variable/inter";
import "@fontsource-variable/jetbrains-mono";
import "./styles/globals.css";

import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { createRoot } from "react-dom/client";
import { queryClient } from "./lib/query.ts";
import { router } from "./router.tsx";
import {
  demoLoginConfigured,
  demoSignIn,
  loginPreviewEnabled,
  signOut,
} from "./lib/auth.ts";
import { initTheme } from "./store/ui.ts";
import { applyBrandTheme } from "./lib/organization-theme.ts";
import { GlobalFeedback } from "./components/global-feedback.tsx";
import { FullScreenLoader } from "./components/full-screen-loader.tsx";
import { initializePlatform } from "./lib/platform.ts";
import { trackMetaPageViewOnce } from "./lib/meta-pixel.ts";
import { medirNavegacion } from "./lib/posthog.ts";

initTheme();
// La marca, antes del primer pintado y sin esperar a que haya sesión: el
// ingreso se dibuja fuera del shell y salía con los colores por defecto del
// sistema de diseño.
applyBrandTheme();
document.documentElement.dataset.release = __RELEASE_SHA__;

// El píxel vive en la web-app para que Meta reconozca el recorrido de alta,
// pero no en las pantallas clínicas: sus URLs pueden identificar recursos de
// pacientes y no forman parte de la medición publicitaria.
if (["/login", "/signup", "/onboarding"].includes(location.pathname)) {
  trackMetaPageViewOnce();
}

/**
 * Un despliegue mientras la app está abierta.
 *
 * Las pantallas se cargan de a pedazos, y cada pedazo lleva su huella en el
 * nombre. Un despliegue cambia esas huellas, así que una pestaña abierta desde
 * antes sigue pidiendo los archivos que ya no están. El servidor, que devuelve
 * la misma página para cualquier ruta, contesta el HTML con un 200; el
 * navegador intenta leerlo como JavaScript y la sección no abre.
 *
 * No es raro: en una clínica la pestaña queda abierta todo el día, y cualquier
 * publicación deja a quien la tenía abierta con una app que falla al entrar a
 * una sección que todavía no había visitado.
 *
 * Recargar trae el índice nuevo y con él las huellas nuevas. Se hace una sola
 * vez por sesión de pestaña: si al recargar el error persiste —no era una
 * versión vieja sino un archivo que de verdad falta— recargar de nuevo sería
 * un ciclo infinito, y es mejor que el error se vea a que la pantalla parpadee
 * para siempre.
 */
const RECARGA_POR_DESPLIEGUE = "ronda:recarga-por-despliegue";
globalThis.addEventListener("vite:preloadError", (event) => {
  if (sessionStorage.getItem(RECARGA_POR_DESPLIEGUE)) return;
  sessionStorage.setItem(RECARGA_POR_DESPLIEGUE, "1");
  event.preventDefault();
  globalThis.location.reload();
});
/*
 * La marca se limpia recién cuando la app lleva un rato andando, y no al
 * terminar de cargar.
 *
 * Limpiarla en `load` era limpiarla en el evento que dispara la propia
 * recarga: con un archivo de verdad roto —no una versión vieja— la vuelta era
 * cargar, limpiar la marca, fallar, recargar, y otra vez, para siempre. El
 * freno no frenaba nada.
 *
 * Con esta espera, el error que aparece al toque de una recarga encuentra la
 * marca todavía puesta y se muestra en vez de volver a recargar. Y media
 * hora después, cuando haya otra publicación, la marca ya no está y la
 * pestaña tiene de nuevo su recarga disponible.
 */
setTimeout(() => sessionStorage.removeItem(RECARGA_POR_DESPLIEGUE), 30_000);

const root = document.getElementById("app");
if (!root) throw new Error("#app is missing from index.html");

// Antes de montar: si el entorno pide ingreso automático, el router tiene que
// encontrar la sesión ya puesta. Montando primero, el guardia manda a /login y
// la demo arranca con un formulario en la cara.
//
// Se borra lo que hubiera antes. Un token viejo de otra sesión no sirve —la API
// lo rechaza y el 401 termina en la pantalla de ingreso— y distinguir uno bueno
// de uno vencido cuesta más que pedir sesión nueva.
//
// Salvo que se haya pedido ver el ingreso: si acá se abre sesión, el guardia de
// la ruta ya no tiene nada que decidir y la pantalla no aparece nunca — y la
// sesión de quien esté recorriendo el alta de una cuenta nueva se pierde en la
// primera recarga.
// En una función y no con un `await` de nivel superior: los navegadores que el
// build declara como objetivo no lo admiten, y `vite build` fallaba al
// transpilar mientras el servidor de desarrollo —que no transpila— lo aceptaba
// sin decir nada. El orden es el mismo: la sesión, y recién después el montaje.
async function boot(host: HTMLElement) {
  const appRoot = createRoot(host);

  await initializePlatform();

  if (demoLoginConfigured() && !loginPreviewEnabled()) {
    // `demoSignIn` ocurre antes del router; sin este estado el documento queda
    // vacío mientras se crea la sesión y todavía no hay layout que mostrar.
    appRoot.render(<FullScreenLoader />);
    signOut();
    await demoSignIn();
  }

  // La medición de producto se engancha antes del montaje para no perderse la
  // primera pantalla. Manda el molde de cada ruta, nunca la dirección: ver
  // `lib/posthog.ts`.
  medirNavegacion(router);

  appRoot.render(
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <GlobalFeedback />
    </QueryClientProvider>,
  );
}

void boot(root);
